/**
 * Self-test suite for the screen-security partial mitigations.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ScreenSecuritySelfTest.swift` where
 * applicable. Web-specific gaps (screenshot detection) are documented inline
 * and in `kaether_for_web/docs/screen-security.md`.
 *
 * THREAT-MODEL-RELEVANT INVARIANTS COVERED:
 *   - Default settings are privacy-preserving: blur ON, detection ON, notify-peer OFF.
 *   - VisibilityBlurPolicy: hidden + blurOnHidden=true → showBlurOverlay.
 *   - VisibilityBlurPolicy: visible → noBlur (regardless of settings).
 *   - VisibilityBlurPolicy: hidden + blurOnHidden=false → noBlur.
 *   - ScreenCaptureDetectionPolicy: per-convo ignore → ignore.
 *   - ScreenCaptureDetectionPolicy: per-convo logAudit → logAudit.
 *   - ScreenCaptureDetectionPolicy: per-convo logAuditAndNotifyPeer, both
 *     global+peer gated → logAuditAndNotifyPeer.
 *   - ScreenCaptureDetectionPolicy: per-convo logAuditAndNotifyPeer but
 *     global=false → downgrade to logAudit.
 *   - ScreenCaptureDetectionPolicy: per-convo logAuditAndNotifyPeer but
 *     peer has NOT opted in → downgrade to logAudit.
 *   - ScreenCaptureDetectionPolicy: global detection disabled → always ignore.
 *   - AuditStore: record + recent round-trip.
 *   - AuditStore: count reflects stored events.
 *   - AuditStore: persistence across separate store instances (same IDB db).
 *   - AuditStore type invariant: no `upload` / `sync` / `sendToServer` method
 *     exists on the public interface or at runtime.
 *
 * Cases (13 total):
 *
 *   A. Default settings
 *    1. defaults: blurOnHidden=true, screenCaptureDetection=true, notifyPeerOnCapture=false
 *
 *   B. VisibilityBlurPolicy
 *    2. hidden + blurOnHidden=true → showBlurOverlay
 *    3. visible + blurOnHidden=true → noBlur
 *    4. hidden + blurOnHidden=false → noBlur
 *
 *   C. ScreenCaptureDetectionPolicy
 *    5. global detection disabled → ignore (regardless of per-convo setting)
 *    6. per-convo=ignore → ignore
 *    7. per-convo=logAudit → logAudit
 *    8. per-convo=logAuditAndNotifyPeer, global=true, peer opted in → logAuditAndNotifyPeer
 *    9. per-convo=logAuditAndNotifyPeer, global=false → downgrade to logAudit
 *   10. per-convo=logAuditAndNotifyPeer, peer NOT opted in → downgrade to logAudit
 *
 *   D. AuditStore
 *   11. record + recent round-trip
 *   12. count reflects stored events
 *   13. persistence: write → reopen same DB → retrievable
 *
 *   E. API surface invariant
 *   14. AuditStore has no upload / sync / sendToServer method (type + runtime)
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * The runner auto-discovers this file via Vite `import.meta.glob` in
 * `selftest/index.ts` (matches `**\/*SelfTest.ts`).
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  DEFAULT_SCREEN_SECURITY_SETTINGS,
  mergeScreenSecuritySettings,
} from "./ScreenSecuritySettings";
import { decideBlur } from "./VisibilityBlurPolicy";
import { decide } from "./ScreenCaptureDetectionPolicy";
import type { ScreenCaptureEvent } from "./ScreenCaptureDetectionPolicy";
import {
  ScreenSecurityAuditStore,
} from "./ScreenSecurityAuditStore";
import type { IScreenSecurityAuditStore } from "./ScreenSecurityAuditStore";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

let dbCounter = 0;

function freshStore(): ScreenSecurityAuditStore {
  dbCounter += 1;
  return new ScreenSecurityAuditStore(
    `kaether:screen-security:selftest:${dbCounter}:${Date.now()}`,
  );
}

function makeEvent(
  override: Partial<ScreenCaptureEvent> = {},
): ScreenCaptureEvent {
  return {
    detectedAt: Date.now(),
    conversationId: "1on1:peer-selftest",
    peerHasOptedIn: false,
    ...override,
  };
}

// ---------------------------------------------------------------------------
// A. Default settings
// ---------------------------------------------------------------------------

function testDefaultSettings(): TestCase {
  const name = "defaults: blurOnHidden=true, screenCaptureDetection=true, notifyPeerOnCapture=false";
  const s = DEFAULT_SCREEN_SECURITY_SETTINGS;
  const ok =
    s.blurOnHidden === true &&
    s.screenCaptureDetection === true &&
    s.notifyPeerOnCapture === false;
  return ok
    ? pass(name)
    : fail(
        name,
        `unexpected defaults: ${JSON.stringify(s)}`,
      );
}

// ---------------------------------------------------------------------------
// B. VisibilityBlurPolicy
// ---------------------------------------------------------------------------

function testBlurHiddenOn(): TestCase {
  const name = "blur policy: hidden + blurOnHidden=true → showBlurOverlay";
  const decision = decideBlur("hidden", { blurOnHidden: true });
  return decision === "showBlurOverlay"
    ? pass(name)
    : fail(name, `got ${decision}`);
}

function testBlurVisible(): TestCase {
  const name = "blur policy: visible + blurOnHidden=true → noBlur";
  const decision = decideBlur("visible", { blurOnHidden: true });
  return decision === "noBlur"
    ? pass(name)
    : fail(name, `got ${decision}`);
}

function testBlurHiddenOff(): TestCase {
  const name = "blur policy: hidden + blurOnHidden=false → noBlur";
  const decision = decideBlur("hidden", { blurOnHidden: false });
  return decision === "noBlur"
    ? pass(name)
    : fail(name, `got ${decision}`);
}

// ---------------------------------------------------------------------------
// C. ScreenCaptureDetectionPolicy
// ---------------------------------------------------------------------------

function testCaptureGlobalDisabled(): TestCase {
  const name = "capture policy: global detection=false → ignore";
  const settings = mergeScreenSecuritySettings({
    screenCaptureDetection: false,
    notifyPeerOnCapture: true,
  });
  const d = decide(makeEvent({ peerHasOptedIn: true }), "logAuditAndNotifyPeer", settings);
  return d === "ignore" ? pass(name) : fail(name, `got ${d}`);
}

function testCapturePerConvoIgnore(): TestCase {
  const name = "capture policy: per-convo=ignore → ignore";
  const settings = mergeScreenSecuritySettings({ screenCaptureDetection: true });
  const d = decide(makeEvent(), "ignore", settings);
  return d === "ignore" ? pass(name) : fail(name, `got ${d}`);
}

function testCapturePerConvoLogAudit(): TestCase {
  const name = "capture policy: per-convo=logAudit → logAudit";
  const settings = mergeScreenSecuritySettings({ screenCaptureDetection: true });
  const d = decide(makeEvent(), "logAudit", settings);
  return d === "logAudit" ? pass(name) : fail(name, `got ${d}`);
}

function testCaptureNotifyPeerBothGated(): TestCase {
  const name =
    "capture policy: per-convo=logAuditAndNotifyPeer, global=true, peer opted in → logAuditAndNotifyPeer";
  const settings = mergeScreenSecuritySettings({
    screenCaptureDetection: true,
    notifyPeerOnCapture: true,
  });
  const d = decide(
    makeEvent({ peerHasOptedIn: true }),
    "logAuditAndNotifyPeer",
    settings,
  );
  return d === "logAuditAndNotifyPeer" ? pass(name) : fail(name, `got ${d}`);
}

function testCaptureNotifyPeerGlobalOff(): TestCase {
  const name =
    "capture policy: per-convo=logAuditAndNotifyPeer, global=false → downgrade to logAudit";
  const settings = mergeScreenSecuritySettings({
    screenCaptureDetection: true,
    notifyPeerOnCapture: false, // global gate closed
  });
  const d = decide(
    makeEvent({ peerHasOptedIn: true }),
    "logAuditAndNotifyPeer",
    settings,
  );
  return d === "logAudit" ? pass(name) : fail(name, `got ${d}`);
}

function testCaptureNotifyPeerNotOptedIn(): TestCase {
  const name =
    "capture policy: per-convo=logAuditAndNotifyPeer, peer NOT opted in → downgrade to logAudit";
  const settings = mergeScreenSecuritySettings({
    screenCaptureDetection: true,
    notifyPeerOnCapture: true,
  });
  const d = decide(
    makeEvent({ peerHasOptedIn: false }), // peer opt-in gate closed
    "logAuditAndNotifyPeer",
    settings,
  );
  return d === "logAudit" ? pass(name) : fail(name, `got ${d}`);
}

// ---------------------------------------------------------------------------
// D. AuditStore — record + recent round-trip
// ---------------------------------------------------------------------------

async function testAuditRecordAndRecent(): Promise<TestCase> {
  const name = "audit store: record + recent round-trip";
  try {
    const store = freshStore();
    await store.record({
      kind: "captureDetected",
      conversationId: "1on1:test-peer",
      detail: "selftest",
    });
    const events = await store.recent(10);
    const ok =
      events.length === 1 &&
      events[0].kind === "captureDetected" &&
      events[0].conversationId === "1on1:test-peer";
    return ok
      ? pass(name)
      : fail(name, `unexpected events: ${JSON.stringify(events)}`);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// D. AuditStore — count
// ---------------------------------------------------------------------------

async function testAuditCount(): Promise<TestCase> {
  const name = "audit store: count reflects stored events";
  try {
    const store = freshStore();
    const before = await store.count();
    await store.record({ kind: "blurApplied" });
    await store.record({ kind: "blurLifted" });
    const after = await store.count();
    const ok = before === 0 && after === 2;
    return ok
      ? pass(name)
      : fail(name, `before=${before} after=${after}`);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// D. AuditStore — persistence across instances
// ---------------------------------------------------------------------------

async function testAuditPersistence(): Promise<TestCase> {
  const name = "audit store: persistence — write → reopen same DB → retrievable";
  const dbName = `kaether:screen-security:selftest:persist:${Date.now()}`;
  try {
    // Write in first instance.
    const store1 = new ScreenSecurityAuditStore(dbName);
    await store1.record({
      kind: "peerNotified",
      conversationId: "1on1:persistence-test",
      detail: "persisted-event",
    });

    // Read from a second instance pointing to the same DB.
    const store2 = new ScreenSecurityAuditStore(dbName);
    const events = await store2.recent(5);
    const ok =
      events.length === 1 &&
      events[0].kind === "peerNotified" &&
      events[0].conversationId === "1on1:persistence-test";
    return ok
      ? pass(name)
      : fail(name, `unexpected events: ${JSON.stringify(events)}`);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// E. API surface invariant — no upload / sync / sendToServer
// ---------------------------------------------------------------------------

function testAuditNoUploadSyncAPI(): TestCase {
  const name =
    "audit store API invariant: no upload / sync / sendToServer method exists";
  try {
    const store = freshStore();
    // Runtime check: the instance must not have these methods.
    const hasUpload = typeof (store as unknown as Record<string, unknown>)["upload"] === "function";
    const hasSync = typeof (store as unknown as Record<string, unknown>)["sync"] === "function";
    const hasSendToServer =
      typeof (store as unknown as Record<string, unknown>)["sendToServer"] === "function";

    // Type-level check via the interface: IScreenSecurityAuditStore only declares
    // `record`, `recent`, `count`. We verify the runtime instance as a proxy for
    // the type surface (TypeScript types are erased at runtime, but the class
    // implementation should match the interface it claims to implement).
    //
    // Additionally, verify the allow-listed methods ARE present.
    const hasRecord = typeof store.record === "function";
    const hasRecent = typeof store.recent === "function";
    const hasCount = typeof store.count === "function";

    const badMethodFound = hasUpload || hasSync || hasSendToServer;
    const allowListPresent = hasRecord && hasRecent && hasCount;

    if (badMethodFound) {
      const found = [
        hasUpload ? "upload" : null,
        hasSync ? "sync" : null,
        hasSendToServer ? "sendToServer" : null,
      ]
        .filter(Boolean)
        .join(", ");
      return fail(name, `disallowed method(s) found on store: ${found}`);
    }

    if (!allowListPresent) {
      return fail(
        name,
        `allow-listed method missing — record:${hasRecord} recent:${hasRecent} count:${hasCount}`,
      );
    }

    // Type-level: verify the interface IScreenSecurityAuditStore doesn't leak
    // these methods. We do this by casting to the interface and checking the
    // type is narrower (structural check is a no-op at runtime, but documents
    // intent and is checked by the TypeScript compiler).
    const _narrowed: IScreenSecurityAuditStore = store;
    void _narrowed;

    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Screen security (blur + capture-detection policy)",
  isVerified: true,
  run: async (): Promise<ReturnType<typeof buildSuiteResult>> => {
    const cases: TestCase[] = [
      // A. Default settings
      testDefaultSettings(),
      // B. VisibilityBlurPolicy
      testBlurHiddenOn(),
      testBlurVisible(),
      testBlurHiddenOff(),
      // C. ScreenCaptureDetectionPolicy
      testCaptureGlobalDisabled(),
      testCapturePerConvoIgnore(),
      testCapturePerConvoLogAudit(),
      testCaptureNotifyPeerBothGated(),
      testCaptureNotifyPeerGlobalOff(),
      testCaptureNotifyPeerNotOptedIn(),
      // D. AuditStore
      await testAuditRecordAndRecent(),
      await testAuditCount(),
      await testAuditPersistence(),
      // E. API surface invariant
      testAuditNoUploadSyncAPI(),
    ];
    return buildSuiteResult(cases);
  },
});
